// `ProjectEditor` over the live documents: each call is one Automerge change per document it touches, written as the
// Swift writes it — FragmentDocument (fields, comments, word log), ProjectDocument (registry, nodes, checkpoints),
// ProjectEditing (newFragment, move, trash, setFolder, renameFragment and `slot(for:)`), BulkMetadata (setMetadata,
// setIncludeInCompile), Journal (`numberPosts`), DocumentStore (`createCheckpoint`) and the app's save
// (EditorController.performSave: modifiedAt + the day's word log, one change with no message).
//
// Commit messages are the Swift's ("Created", "New fragment “…”", "Moved", "Moved to Trash", "Converted to folder",
// "Renamed", "Checkpoint: …", "Number the post"); edits the app commits with `commit()` (typing, fields, comments) have
// no message. Change times are whole seconds, as automerge-swift records them. Positions come in as UTF-16 (the
// editor's) and are stored as Unicode scalars.
import * as A from '@automerge/automerge';
import type { EditContext, EditProject, FragmentKind, MetadataValue, Placement, ProjectEditor, ProjectType } from '../contract.ts';
import { encodeHeads } from '../automerge/heads.ts';
import { between } from '../automerge/fractionalIndex.ts';
import { scalarLength, scalarOffsetOfUTF16, sliceScalars } from '../automerge/offsets.ts';
import { joinedAt } from '../automerge/joined.ts';
import { ROOT, SwiftReader } from '../automerge/swiftDoc.ts';
import { orderedFragmentIDs, resolvedParent, tree as buildTree } from '../model/arrangement.ts';
import { readProject, type Arrangement, type ArrangementNode, type ProjectRecord } from '../model/documents.ts';
import { convertingNodes, demotedPosts, journalPosts, movingNodes } from '../model/posts.ts';
import { swiftCompare } from '../model/order.ts';
import { joinTags, parseTags, TAGS_FIELD } from '../tags.ts';
import { effectiveLocks, LockedError } from '../locks.ts';
import { isNumberedChapter, NUMBERED_FIELD } from '../numbering.ts';
import { Maps } from '../maps/map.ts';
import { writeMapKeys } from '../maps/mapDocument.ts';
import { ModelError } from './modelError.ts';
import { bool, changeTime, f64, int, str, timestamp, uint, Writer, type Scalar } from './typed.ts';

const FRAGMENT = 'fragment.';
export const fragmentDocName = (id: string) => FRAGMENT + id;

/** `IDs.make()`: a lowercase UUID. */
export const makeID = () => crypto.randomUUID().toLowerCase();

/** `WritingDay.key`: yyyy-MM-dd in the zone the date is read in (the runtime's). */
/** A title is one line (`FragmentDocument.oneLineTitle`): the lines of one that isn't are trimmed, empty ones dropped, a
 *  line repeating the one before it dropped too (a title pasted twice over a break), and the rest joined with spaces.
 *  The contents, the chapter opener and compile's headings draw the title whole, so a break inside it doubled the
 *  heading. */
export function oneLineTitle(title: string): string {
  if (!/[\r\n]/.test(title)) return title.trim();
  const lines: string[] = [];
  for (const raw of title.split(/[\r\n]+/)) {
    const line = raw.trim();
    if (line && line !== lines[lines.length - 1]) lines.push(line);
  }
  return lines.join(' ');
}

export function writingDay(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${String(d.getFullYear()).padStart(4, '0')}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** `StandardField` ids the writes depend on. */
const Field = { status: 'status', wordTarget: 'word-target', postNumber: 'post-number', publishDate: 'publish-date' } as const;

/** `JournalPosts`. */
export const JournalPosts = {
  idea: 'status-idea',
  draft: 'status-draft',
  scheduled: 'status-scheduled',
  published: 'status-published',
  numberLimit: 1_000_000_000,
  /** `number(in:)`: a whole number of at least 1. */
  number(metadata: Record<string, MetadataValue> | undefined): number | null {
    const v = metadata?.[Field.postNumber];
    if (v?.type === 'number') return Number.isFinite(v.value) && v.value >= 1 && v.value < 1e9 ? Math.round(v.value) : null;
    if (v?.type === 'text') {
      const t = v.value.replace(/^[ \t]+|[ \t]+$/g, '');
      if (!/^[+-]?\d+$/.test(t)) return null;
      const n = Number(t);
      return n >= 1 && n < 1_000_000_000 ? n : null;
    }
    return null;
  },
};

/** `WordTarget.stored`: a valid target as a number, anything else cleared. */
export function storedWordTarget(value: MetadataValue | null): MetadataValue | null {
  if (!value) return null;
  const limit = 1_000_000_000;
  if (value.type === 'number') {
    const n = value.value;
    // Swift's `rounded()` is schoolbook rounding (half away from zero); n ≥ 1 here.
    return Number.isFinite(n) && n >= 1 && n < limit ? { type: 'number', value: Math.round(n) } : null;
  }
  if (value.type === 'text') {
    const digits = [...value.value].filter((c) => c >= '0' && c <= '9').join('');
    if (!digits) return null;
    const n = Number(digits);
    return Number.isSafeInteger(n) && n >= 1 && n < limit ? { type: 'number', value: n } : null;
  }
  return null;
}

/** `MetadataValue.scalar`. */
export function metadataScalar(v: MetadataValue): Scalar {
  switch (v.type) {
    case 'text': return str(v.value);
    case 'number': return f64(v.value);
    case 'boolean': return bool(v.value);
    case 'date': return timestamp(v.value);
  }
}

/** `MetadataValue ==`. */
function sameValue(a: MetadataValue | undefined, b: MetadataValue | null): boolean {
  if (!a || !b) return !a && !b;
  if (a.type !== b.type) return false;
  if (a.type === 'date') return a.value.getTime() === (b.value as Date).getTime();
  return a.value === b.value;
}

/** `MetadataValue(scalar:)` for each scalar entry of the fragment's metadata map (`FragmentDocument.metadata()`). */
export function readMetadata(r: SwiftReader): Record<string, MetadataValue> {
  const out: Record<string, MetadataValue> = {};
  const obj = r.map(ROOT, 'metadata');
  if (!obj) return out;
  for (const [k, [t, v]] of r.entries(obj)) {
    if (t === 'str') out[k] = { type: 'text', value: v as string };
    else if (t === 'f64' || t === 'int' || t === 'uint') out[k] = { type: 'number', value: Number(v) };
    else if (t === 'boolean') out[k] = { type: 'boolean', value: v as boolean };
    else if (t === 'timestamp') out[k] = { type: 'date', value: v as Date };
  }
  return out;
}

/** `Arrangement.subtree(of:)`: the node, then everything under it breadth first, children by (position, id). */
export function subtree(a: Arrangement, nodeID: string): ArrangementNode[] {
  const root = a.nodes.get(nodeID);
  if (!root) return [];
  const children = new Map<string, ArrangementNode[]>();
  for (const node of a.nodes.values()) {
    const p = resolvedParent(a, node);
    if (p !== undefined) children.set(p, [...(children.get(p) ?? []), node]);
  }
  const out = [root];
  const seen = new Set([root.id]);
  for (let i = 0; i < out.length; i++) {
    const kids = (children.get(out[i].id) ?? []).sort((x, y) => swiftCompare(x.position, y.position) || swiftCompare(x.id, y.id));
    for (const c of kids) if (!seen.has(c.id)) { seen.add(c.id); out.push(c); }
  }
  return out;
}

/** `isWithinSubtree`: `candidate` is `nodeID` or under it by stored parent pointers (stops on cycles). */
export function isWithinSubtree(a: Arrangement, candidate: string | null, nodeID: string): boolean {
  const seen = new Set<string>();
  let cursor: string | undefined = candidate ?? undefined;
  while (cursor !== undefined && !seen.has(cursor)) {
    seen.add(cursor);
    if (cursor === nodeID) return true;
    cursor = a.nodes.get(cursor)?.parentID;
  }
  return false;
}

/** `children(of:in:)`: a parent's children as `tree()` orders them (nil = the top level). */
export function childrenOf(a: Arrangement, parentID: string | null): ArrangementNode[] {
  const find = (nodes: ReturnType<typeof buildTree>): ReturnType<typeof buildTree> | null => {
    for (const n of nodes) {
      if (n.node.id === parentID) return n.children;
      const hit = find(n.children);
      if (hit) return hit;
    }
    return null;
  };
  const t = buildTree(a);
  return (parentID === null ? t : (find(t) ?? [])).map((n) => n.node);
}

/** `flattened()` order index of every node. */
function readingOrder(a: Arrangement): Map<string, number> {
  const out = new Map<string, number>();
  let i = 0;
  const walk = (nodes: ReturnType<typeof buildTree>) => { for (const n of nodes) { if (!out.has(n.node.id)) out.set(n.node.id, i++); walk(n.children); } };
  walk(buildTree(a));
  return out;
}

/** Where a placement puts something: (parent, index among the parent's children that aren't moving, or null = end). */
function resolvePlacement(a: Arrangement, placement: Placement, moving: Set<string> = new Set()): { parent: string | null; index: number | null } {
  if ('after' in placement) {
    const sibling = a.nodes.get(placement.after);
    if (!sibling) return { parent: null, index: null };
    const parent = sibling.parentID ?? null;
    const siblings = childrenOf(a, parent);
    const at = siblings.findIndex((n) => n.id === sibling.id);
    return { parent, index: siblings.slice(0, at + 1).filter((n) => !moving.has(n.id)).length };
  }
  if ('first' in placement) return { parent: null, index: 0 };
  return { parent: placement.inside, index: placement.index ?? null };
}

/** `ArrangementNode` as `putNode` writes it. */
export function putNode(w: Writer, arrangementID: string, node: ArrangementNode) {
  const path = ['arrangements', arrangementID, 'nodes', node.id];
  w.putOptional(path, 'fragment', node.fragmentID);
  w.putOptional(path, 'title', node.title);
  w.putIfChanged(path, 'folder', bool(node.isFolder));
  w.putOptional(path, 'parent', node.parentID);
  w.putIfChanged(path, 'position', str(node.position));
}

/** `EditContext` plus what tests and fixtures pin: ids and the actor of new documents (random by default). */
export interface EditOptions extends EditContext {
  makeID?: () => string;
  /** The actor (hex) a new fragment document is made with. */
  newActor?: () => string;
  /** The actor (hex) every change to an existing document is made as (the document is copied to it first); by default
   *  each document keeps the actor it was loaded with. */
  actor?: string;
  /** Copy each document before its first change here (as `actor`, else a new actor), leaving the documents given
   *  untouched and still writable: Automerge makes a document outdated once it has been changed. */
  copyOnWrite?: boolean;
}

export const editProject: EditProject = (docs, context) => new DocumentEditor(docs, context);

export class DocumentEditor implements ProjectEditor {
  constructor(
    readonly docs: Map<string, A.Doc<unknown>>,
    readonly context: EditOptions,
  ) {}

  protected id(): string {
    return this.context.makeID?.() ?? makeID();
  }

  protected now(): Date {
    return this.context.now?.() ?? new Date();
  }

  protected doc(name: string): A.Doc<unknown> {
    const d = this.docs.get(name);
    if (!d) throw new Error(`No document “${name}”`);
    return d;
  }

  protected fragmentDoc(id: string): A.Doc<unknown> {
    return this.doc(fragmentDocName(id));
  }

  /** One change to one document: `fn` writes through a `Writer` over the proxy; returns whether anything changed. */
  protected change(name: string, message: string | undefined, at: Date, fn: (w: Writer, r: SwiftReader, d: Record<string, any>) => void): boolean {
    let doc = this.doc(name);
    const actor = this.context.actor;
    if (this.context.copyOnWrite && !this.written.has(doc)) doc = A.clone(doc, actor ? { actor } : undefined);
    else if (actor && A.getActorId(doc) !== actor) doc = A.clone(doc, { actor });
    const r = new SwiftReader(doc);
    const before = A.getHeads(doc);
    const opts = message === undefined ? { time: changeTime(at) } : { message, time: changeTime(at) };
    const next = A.change(doc as A.Doc<Record<string, any>>, opts, (d) => fn(new Writer(d, r), r, d));
    const after = A.getHeads(next);
    if (after.length === before.length && after.every((h, i) => h === before[i])) return false;
    this.docs.set(name, next as A.Doc<unknown>);
    this.written.add(next);
    return true;
  }

  /** Documents this editor made or changed (copy-on-write copies them once). */
  protected readonly written = new WeakSet<object>();

  protected project(): ProjectRecord {
    return readProject(this.doc('project'));
  }

  protected arrangement(project: ProjectRecord, id: string): Arrangement {
    const a = project.arrangements.find((x) => x.id === id);
    if (!a) throw new Error(`Not found: arrangement ${id}`);
    return a;
  }

  /** `updateFragment`: a change, then `modifiedAt`, committed with no message. */
  protected updateFragment(id: string, fn: (w: Writer, r: SwiftReader, d: Record<string, any>) => void): string[] {
    const at = this.now();
    const name = fragmentDocName(id);
    return this.change(name, undefined, at, (w, r, d) => {
      fn(w, r, d);
      w.putIfChanged([], 'modifiedAt', timestamp(at));
    }) ? [name] : [];
  }

  // MARK: Locks

  /** The fragments under lock now: locked themselves, or under a locked folder (`DocumentStore.effectiveLocks`). */
  protected locks(): Set<string> {
    const own = new Set<string>();
    for (const [name, doc] of this.docs) {
      if (name.startsWith(FRAGMENT) && new SwiftReader(doc).bool(ROOT, 'locked')) own.add(name.slice(FRAGMENT.length));
    }
    return effectiveLocks(this.project().arrangements, own);
  }

  /** Throws `LockedError` naming the first of these fragments that's under lock. */
  protected requireUnlocked(fragmentIds: Iterable<string>) {
    const locks = this.locks();
    if (!locks.size) return;
    for (const id of fragmentIds) {
      if (!locks.has(id)) continue;
      const doc = this.docs.get(fragmentDocName(id));
      throw new LockedError(doc ? new SwiftReader(doc).string(ROOT, 'title') ?? '' : '');
    }
  }

  /** The same for nodes: their fragments and everything under them. */
  protected requireUnlockedNodes(a: Arrangement, nodeIds: Iterable<string>) {
    const ids: string[] = [];
    for (const nodeId of nodeIds) for (const n of subtree(a, nodeId)) if (n.fragmentID) ids.push(n.fragmentID);
    this.requireUnlocked(ids);
  }

  /** `refuseMaps`: throws when any of these nodes, or anything under them, is a map: they're about to go somewhere other
   *  than Research & Notes. */
  protected refuseMaps(a: Arrangement, nodeIds: Iterable<string>, project: ProjectRecord = this.project()) {
    for (const nodeId of nodeIds) {
      for (const n of subtree(a, nodeId)) {
        if (n.fragmentID !== undefined && project.fragments.get(n.fragmentID)?.kind === 'map') throw new ModelError('invalid', Maps.staysInResearch);
      }
    }
  }

  /** `FragmentDocument.setLocked`: locks or unlocks a document (and so, for a folder, everything under it). */
  setLocked(fragmentId: string, locked: boolean): string[] {
    const name = fragmentDocName(fragmentId);
    const r = new SwiftReader(this.fragmentDoc(fragmentId));
    if ((r.bool(ROOT, 'locked') ?? false) === locked) return [];
    return this.change(name, locked ? 'Locked' : 'Unlocked', this.now(), (w) => {
      if (locked) w.put([], 'locked', bool(true));
      else w.delete([], 'locked');
    }) ? [name] : [];
  }

  // MARK: Text and fields

  spliceText(fragmentId: string, utf16Start: number, deleteCount: number, insert: string): string[] {
    if (deleteCount === 0 && insert === '') return [];
    this.requireUnlocked([fragmentId]);
    const name = fragmentDocName(fragmentId);
    // Typing isn't committed with a message, and modifiedAt waits for the save (`recordWords`), as on the Mac.
    return this.change(name, undefined, this.now(), (_w, r, d) => {
      if (r.get(ROOT, 'text')?.[0] !== 'text') d.text = '';
      const length = (d.text as string).length;
      const start = Math.max(0, Math.min(utf16Start, length));
      A.splice(d, ['text'], start, Math.max(0, Math.min(deleteCount, length - start)), insert);
    }) ? [name] : [];
  }

  private setTextField(fragmentId: string, key: 'synopsis' | 'notes', text: string): string[] {
    const r = new SwiftReader(this.fragmentDoc(fragmentId));
    const stored = r.get(ROOT, key);
    if (stored?.[0] === 'text' && r.text(stored[1] as string) === text) return [];
    this.requireUnlocked([fragmentId]);
    return this.updateFragment(fragmentId, (_w, rr, d) => {
      // `setText`: a text object (made if missing), updated by a minimal diff so cursors hold.
      if (rr.get(ROOT, key)?.[0] !== 'text') d[key] = '';
      A.updateText(d as A.Doc<unknown>, [key], text);
    });
  }

  setTitle(fragmentId: string, title: string): string[] {
    title = oneLineTitle(title);
    if (new SwiftReader(this.fragmentDoc(fragmentId)).get(ROOT, 'title')?.[1] === title) return [];
    this.requireUnlocked([fragmentId]);
    return this.updateFragment(fragmentId, (w) => w.putIfChanged([], 'title', str(title)));
  }

  setSynopsis(fragmentId: string, text: string): string[] {
    return this.setTextField(fragmentId, 'synopsis', text);
  }

  setNotes(fragmentId: string, text: string): string[] {
    return this.setTextField(fragmentId, 'notes', text);
  }

  setIncludeInCompile(fragmentId: string, include: boolean): string[] {
    const r = new SwiftReader(this.fragmentDoc(fragmentId));
    if ((r.bool(ROOT, 'includeInCompile') ?? true) === include) return [];
    return this.updateFragment(fragmentId, (w) => w.putIfChanged([], 'includeInCompile', bool(include)));
  }

  /** `FragmentDocument.setNumbered`: whether a chapter or part takes a number — the marker `numbered: false` set (a
   *  prologue) or removed; compile and the contents count past an unnumbered one. */
  setNumbered(fragmentId: string, numbered: boolean): string[] {
    const current = readMetadata(new SwiftReader(this.fragmentDoc(fragmentId)));
    if (isNumberedChapter(current) === numbered) return [];
    return this.updateFragment(fragmentId, (w) => {
      if (numbered) w.delete(['metadata'], NUMBERED_FIELD);
      else w.putIfChanged(['metadata'], NUMBERED_FIELD, bool(false));
    });
  }

  /** `FragmentDocument.setPresence`: an entity's explicit presence in this fragment (the `presence` map, made on first
   *  use and read joined); null clears it back to inferred. */
  setPresence(fragmentId: string, entityId: string, level: string | null): string[] {
    return this.updateFragment(fragmentId, (w) => {
      if (level !== null) w.put(['presence'], entityId, str(level));
      else w.delete(['presence'], entityId);
    });
  }

  setMetadata(fragmentId: string, fieldId: string, value: MetadataValue | null): string[] {
    // A tags field takes its tags as text ("a, b") and stores them tidied; none clears it.
    if (this.project().fields.some((f) => f.id === fieldId && f.type === 'tags')) {
      return this.setTags(fragmentId, value?.type === 'text' ? parseTags(value.value) : [], fieldId);
    }
    const stored = fieldId === Field.wordTarget ? storedWordTarget(value) : value;
    const current = readMetadata(new SwiftReader(this.fragmentDoc(fragmentId)));
    if (sameValue(current[fieldId], stored)) return [];
    const changed = this.updateFragment(fragmentId, (w) => {
      if (stored) w.putIfChanged(['metadata'], fieldId, metadataScalar(stored));
      else w.delete(['metadata'], fieldId);
    });
    if (changed.length && fieldId === Field.status) for (const n of this.numberPosts([fragmentId])) if (!changed.includes(n)) changed.push(n);
    return changed;
  }

  /** `FragmentDocument.setTags`: the document's tags stored as one list (none removes the value). Where two devices'
   *  values stand at once the list is written even if it's the one shown, which makes them one. */
  setTags(fragmentId: string, tags: readonly string[], fieldId: string = TAGS_FIELD): string[] {
    const value = joinTags(tags);
    const r = new SwiftReader(this.fragmentDoc(fragmentId));
    const metadata = r.map(ROOT, 'metadata');
    const stored = metadata ? ((r.backend.getAll(metadata, fieldId) as unknown as [string, unknown, string][]) ?? []) : [];
    if (value === '') return stored.length ? this.updateFragment(fragmentId, (w) => w.delete(['metadata'], fieldId)) : [];
    if (stored.length === 1 && stored[0][0] === 'str' && stored[0][1] === value) return [];
    return this.updateFragment(fragmentId, (w) => w.put(['metadata'], fieldId, str(value)));
  }

  /** `DocumentStore.numberPosts`: a journal post scheduled or published takes the next number (and, published, today). */
  numberPosts(fragmentIds: string[]): string[] {
    const project = this.project();
    if (project.projectType !== 'journal') return [];
    const wanted = new Map<string, Record<string, MetadataValue>>();
    for (const id of new Set(fragmentIds)) {
      const doc = this.docs.get(fragmentDocName(id));
      if (!doc) continue;
      const metadata = readMetadata(new SwiftReader(doc));
      const status = metadata[Field.status];
      if (status?.type === 'text' && (status.value === JournalPosts.scheduled || status.value === JournalPosts.published)) wanted.set(id, metadata);
    }
    if (!wanted.size) return [];
    // Only posts take numbers: a section keeps a Status it had as a post, but isn't one now. Anything outside the
    // manuscript is numbered after its posts, as before.
    const manuscript = project.arrangements.find((a) => a.role === 'manuscript');
    const order = manuscript ? journalPosts(manuscript).map((p) => p.fragmentId) : [];
    if (manuscript) {
      const inManuscript = new Set(orderedFragmentIDs(manuscript)), posts = new Set(order);
      for (const id of [...wanted.keys()]) if (inManuscript.has(id) && !posts.has(id)) wanted.delete(id);
    }
    if (!wanted.size) return [];
    const rank = new Map<string, number>();
    order.forEach((id, i) => { if (!rank.has(id)) rank.set(id, i); });
    // Oldest first: lowest in the arrangement first; anything outside it after, by id.
    const ids = [...wanted.keys()].sort((a, b) => (-(rank.get(a) ?? -1)) - (-(rank.get(b) ?? -1)) || swiftCompare(a, b));
    let highest = 0;
    for (const id of project.fragments.keys()) {
      const doc = this.docs.get(fragmentDocName(id));
      const n = doc ? JournalPosts.number(readMetadata(new SwiftReader(doc))) : null;
      if (n !== null) highest = Math.max(highest, n);
    }
    const raw = project.settings.firstPostNumber;
    const first = typeof raw === 'number' && raw > 1 && raw < JournalPosts.numberLimit ? raw : 1;
    let next = Math.max(highest + 1, first);
    const out: string[] = [];
    for (const id of ids) {
      const metadata = wanted.get(id)!;
      const at = this.now();
      let number: number | null = null;
      if (JournalPosts.number(metadata) === null) number = next++;
      const status = metadata[Field.status];
      const date = status?.type === 'text' && status.value === JournalPosts.published && !metadata[Field.publishDate]
        ? new Date(at.getFullYear(), at.getMonth(), at.getDate()) : null;
      if (number === null && !date) continue;
      const name = fragmentDocName(id);
      if (this.change(name, 'Number the post', at, (w) => {
        if (number !== null) w.putIfChanged(['metadata'], Field.postNumber, f64(number));
        if (date) w.putIfChanged(['metadata'], Field.publishDate, timestamp(date));
        w.putIfChanged([], 'modifiedAt', timestamp(at));
      })) out.push(name);
    }
    return out;
  }

  /** The app's save of typed text (EditorController.performSave): `modifiedAt`, then this device's word log. */
  recordWords(fragmentId: string, added: number, removed: number): string[] {
    const at = this.now();
    const day = writingDay(at);
    const name = fragmentDocName(fragmentId);
    return this.change(name, undefined, at, (w, r) => {
      w.putIfChanged([], 'modifiedAt', timestamp(at));
      if (!(added > 0 || removed > 0)) return;
      // The log is made on first use, so each of two devices may have made one: the total so far is read from
      // whichever holds it (`JoinedMap`).
      const log = joinedAt(r, ROOT, 'wordLog');
      for (const [suffix, amount] of [['+', added], ['-', removed]] as const) {
        if (!(amount > 0)) continue;
        const key = `${day} ${this.context.device} ${suffix}`;
        const before = log.int(key) ?? 0;
        w.put(['wordLog'], key, int(before + Math.round(amount)));
      }
    }) ? [name] : [];
  }

  // MARK: Comments

  private putComment(w: Writer, c: {
    id: string; author: string; authorKind: 'human' | 'agent'; body: string; anchorStart: number; anchorEnd: number;
    anchorHeads: string; quote: string; parentId: string | null; status: 'open' | 'resolved'; createdAt: Date;
  }) {
    const path = ['comments', c.id];
    w.putIfChanged(path, 'author', str(c.author));
    w.putIfChanged(path, 'authorKind', str(c.authorKind));
    w.putIfChanged(path, 'body', str(c.body));
    w.putIfChanged(path, 'anchorStart', uint(c.anchorStart));
    w.putIfChanged(path, 'anchorEnd', uint(c.anchorEnd));
    w.putIfChanged(path, 'anchorHeads', str(c.anchorHeads));
    w.putIfChanged(path, 'quote', str(c.quote));
    w.putOptional(path, 'parent', c.parentId);
    w.putIfChanged(path, 'status', str(c.status));
    w.putIfChanged(path, 'createdAt', timestamp(c.createdAt));
    w.putOptional(path, 'image', null);
  }

  addComment(fragmentId: string, utf16Start: number, utf16End: number, body: string): { id: string; changed: string[] } {
    const doc = this.fragmentDoc(fragmentId);
    const text = new SwiftReader(doc).string(ROOT, 'text') ?? '';
    const scalars = scalarLength(text);
    const lower = Math.min(scalarOffsetOfUTF16(text, Math.max(0, utf16Start)), scalars);
    const upper = Math.max(lower, Math.min(scalarOffsetOfUTF16(text, Math.max(0, utf16End)), scalars));
    const id = this.id();
    const createdAt = this.now();
    const anchorHeads = encodeHeads(A.getHeads(doc));
    const changed = this.updateFragment(fragmentId, (w) => this.putComment(w, {
      id, author: this.context.author, authorKind: 'human', body, anchorStart: lower, anchorEnd: upper, anchorHeads,
      quote: sliceScalars(text, lower, upper), parentId: null, status: 'open', createdAt,
    }));
    return { id, changed };
  }

  reply(fragmentId: string, parentId: string, body: string): { id: string; changed: string[] } {
    const r = new SwiftReader(this.fragmentDoc(fragmentId));
    const all = r.map(ROOT, 'comments');
    const parent = all ? r.map(all, parentId) : undefined;
    if (!parent) throw new Error(`Not found: comment ${parentId}`);
    const id = this.id();
    const createdAt = this.now();
    // A reply is anchored where its comment is (the same passage at the same heads).
    const changed = this.updateFragment(fragmentId, (w) => this.putComment(w, {
      id, author: this.context.author, authorKind: 'human', body,
      anchorStart: r.uint(parent, 'anchorStart') ?? 0, anchorEnd: r.uint(parent, 'anchorEnd') ?? 0,
      anchorHeads: r.string(parent, 'anchorHeads') ?? '', quote: r.string(parent, 'quote') ?? '', parentId, status: 'open', createdAt,
    }));
    return { id, changed };
  }

  setCommentStatus(fragmentId: string, commentId: string, status: 'open' | 'resolved'): string[] {
    const r = new SwiftReader(this.fragmentDoc(fragmentId));
    const all = r.map(ROOT, 'comments');
    if (!all || !r.map(all, commentId)) throw new Error(`Not found: comment ${commentId}`);
    return this.updateFragment(fragmentId, (w) => w.putIfChanged(['comments', commentId], 'status', str(status)));
  }

  removeComment(fragmentId: string, commentId: string): string[] {
    return this.updateFragment(fragmentId, (w) => w.delete(['comments'], commentId));
  }

  /** `setCommentCategory`: a comment on the text or the synopsis put in a category of the project's (null: in none).
   *  Only this clears a category; writing the comment again never does. */
  setCommentCategory(fragmentId: string, commentId: string, categoryId: string | null): string[] {
    const r = new SwiftReader(this.fragmentDoc(fragmentId));
    for (const key of ['comments', 'synopsisComments']) {
      if (!joinedAt(r, ROOT, key).map(commentId)) continue;
      return this.updateFragment(fragmentId, (w) => {
        if (categoryId === null) w.putOptional([key, commentId], 'category', null);
        else w.putIfChanged([key, commentId], 'category', str(categoryId));
      });
    }
    throw new Error(`Not found: comment ${commentId}`);
  }

  /** `setSynopsisCommentStatus`: a comment on the synopsis (`synopsisComments`) resolved or reopened. */
  setSynopsisCommentStatus(fragmentId: string, commentId: string, status: 'open' | 'resolved'): string[] {
    const r = new SwiftReader(this.fragmentDoc(fragmentId));
    if (!joinedAt(r, ROOT, 'synopsisComments').map(commentId)) throw new Error(`Not found: comment ${commentId}`);
    return this.updateFragment(fragmentId, (w) => w.putIfChanged(['synopsisComments', commentId], 'status', str(status)));
  }

  /** `setSuggestionStatus`: accepted or rejected (Accept applies the text first, through `spliceText`). */
  setSuggestionStatus(fragmentId: string, suggestionId: string, status: 'open' | 'accepted' | 'rejected'): string[] {
    const r = new SwiftReader(this.fragmentDoc(fragmentId));
    const all = r.map(ROOT, 'suggestions');
    if (!all || !r.map(all, suggestionId)) throw new Error(`Not found: suggestion ${suggestionId}`);
    return this.updateFragment(fragmentId, (w) => w.putIfChanged(['suggestions', suggestionId], 'status', str(status)));
  }

  /** `setModifiedAt` + `commit(message)`: a store rewrite's closing change (Replace: "Replace “…”"). */
  touch(fragmentId: string, message?: string): string[] {
    const at = this.now();
    const name = fragmentDocName(fragmentId);
    return this.change(name, message, at, (w) => w.put([], 'modifiedAt', timestamp(at))) ? [name] : [];
  }

  // MARK: Structure

  /** `slot(for:)` (with an index for `inside`, as `move` places): the parent and a position between neighbours. */
  protected slot(a: Arrangement, placement: Placement): { parent: string | null; position: string } {
    if ('after' in placement) {
      const sibling = a.nodes.get(placement.after);
      if (!sibling) {
        const top = buildTree(a);
        return { parent: null, position: between(top.at(-1)?.node.position, undefined) };
      }
      const siblings = childrenOf(a, sibling.parentID ?? null);
      const i = siblings.findIndex((n) => n.id === sibling.id);
      const next = i >= 0 && i + 1 < siblings.length ? siblings[i + 1] : undefined;
      return { parent: sibling.parentID ?? null, position: between(sibling.position, next?.position) };
    }
    if ('first' in placement) {
      return { parent: null, position: between(undefined, childrenOf(a, null)[0]?.position) };
    }
    const siblings = childrenOf(a, placement.inside);
    if (placement.index === undefined) return { parent: placement.inside, position: between(siblings.at(-1)?.position, undefined) };
    const i = Math.max(0, Math.min(placement.index, siblings.length));
    return { parent: placement.inside, position: between(i > 0 ? siblings[i - 1].position : undefined, siblings[i]?.position) };
  }

  newFragment(o: { arrangementId: string; placement: Placement; title?: string; kind?: FragmentKind; isFolder?: boolean; text?: string;
    /** A journal's new post starts as a draft (the app's New Post); false for the store's own `newFragment`, which
     *  sets no status (agent drafts, captures). */
    post?: boolean }):
    { fragmentId: string; nodeId: string; changed: string[] } {
    const project = this.project();
    const a = this.arrangement(project, o.arrangementId);
    const title = o.title ?? 'Untitled';
    const isFolder = o.isFolder ?? false;
    // A screenplay's scenes are Fountain; its notes and research stay prose (LibraryModel.newFragment).
    const manuscriptKind = (t: ProjectType): FragmentKind => (t === 'screenplay' ? 'screenplay' : 'prose');
    const kind = o.kind ?? (a.role === 'manuscript' || a.role === 'alternate' ? manuscriptKind(project.projectType) : 'prose');
    // A journal's new post starts as a draft; a section placed in a post (`journalPosts`) is no post and has no status.
    const { parent, position } = this.slot(a, o.placement);
    const inPost = parent !== null && journalPosts(a).some((p) => p.nodeId === parent || p.nodes.has(parent));
    const post = (o.post ?? true) && project.projectType === 'journal' && a.role === 'manuscript' && !isFolder && !inPost;
    const createdAt = this.now();
    const fragmentId = this.id();
    const nodeId = this.id();

    // FragmentDocument.create, key for key and type for type, plus the text and metadata, committed "Created".
    const actor = this.context.newActor?.();
    const fragment = A.change(A.init<Record<string, any>>(actor ? { actor } : undefined), { message: 'Created', time: changeTime(createdAt) }, (d) => {
      const w = new Writer(d, new SwiftReader(A.init()));
      w.put([], 'schemaVersion', int(1));
      w.put([], 'type', str('fragment'));
      w.put([], 'id', str(fragmentId));
      w.put([], 'projectId', str(project.id));
      w.put([], 'epoch', int(0));
      w.put([], 'kind', str(kind));
      w.put([], 'title', str(oneLineTitle(title)));
      d.synopsis = '';
      d.text = '';
      d.notes = '';
      d.metadata = {};
      w.put([], 'includeInCompile', bool(true));
      w.put([], 'createdAt', timestamp(createdAt));
      w.put([], 'modifiedAt', timestamp(createdAt));
      d.comments = {};
      d.suggestions = {};
      d.attachments = [];
      if (kind === 'map') writeMapKeys(w);
      if (o.text) A.splice(d, ['text'], 0, 0, o.text);
      if (post) w.put(['metadata'], Field.status, str(JournalPosts.draft));
    });
    const name = fragmentDocName(fragmentId);
    this.docs.set(name, fragment as A.Doc<unknown>);
    this.written.add(fragment);

    this.change('project', `New ${isFolder ? 'folder' : 'fragment'} “${title}”`, createdAt, (w) => {
      // registerFragment
      const reg = ['fragments', fragmentId];
      w.putIfChanged(reg, 'kind', str(kind));
      w.putIfChanged(reg, 'trashed', bool(false));
      w.put(reg, 'createdAt', timestamp(createdAt));
      putNode(w, o.arrangementId, { id: nodeId, fragmentID: fragmentId, isFolder, parentID: parent ?? undefined, position });
    });
    return { fragmentId, nodeId, changed: [name, 'project'] };
  }

  renameFragment(fragmentId: string, title: string): string[] {
    this.requireUnlocked([fragmentId]);
    const name = fragmentDocName(fragmentId);
    return this.change(name, 'Renamed', this.now(), (w) => w.putIfChanged([], 'title', str(oneLineTitle(title)))) ? [name] : [];
  }

  /** `move(_:from:to:parent:index:)` within one arrangement: the top nodes in reading order, placed together. */
  move(nodeIds: string[], arrangementId: string, placement: Placement): string[] {
    const project = this.project();
    const a = this.arrangement(project, arrangementId);
    if (a.role === 'trash') throw new Error('Use Move to Trash to put things in the Trash.');
    this.requireUnlockedNodes(a, nodeIds);
    const order = readingOrder(a);
    const requested = new Set(nodeIds);
    const ancestorsOf = (id: string) => {
      const out: string[] = [];
      const seen = new Set([id]);
      let n = a.nodes.get(id);
      let p = n ? resolvedParent(a, n) : undefined;
      while (p !== undefined && !seen.has(p)) {
        seen.add(p);
        out.push(p);
        n = a.nodes.get(p);
        p = n ? resolvedParent(a, n) : undefined;
      }
      return out;
    };
    const tops = [...requested].filter((id) => a.nodes.has(id) && !ancestorsOf(id).some((x) => requested.has(x)))
      .sort((x, y) => (order.get(x) ?? Number.MAX_SAFE_INTEGER) - (order.get(y) ?? Number.MAX_SAFE_INTEGER) || swiftCompare(x, y));
    if (!tops.length) return [];
    const moving = new Set(tops);
    const { parent, index } = resolvePlacement(a, placement, moving);
    if (parent !== null && !a.nodes.has(parent)) throw new Error(`Not found: folder ${parent}`);
    if (tops.some((t) => isWithinSubtree(a, parent, t))) throw new Error('A folder can’t be moved into itself.');
    this.keepPosts(a, movingNodes(a, tops, parent));
    const siblings = childrenOf(a, parent).filter((n) => !moving.has(n.id));
    const slot = Math.max(0, Math.min(index ?? siblings.length, siblings.length));
    let lower = slot > 0 ? siblings[slot - 1].position : undefined;
    const upper = slot < siblings.length ? siblings[slot].position : undefined;
    return this.change('project', 'Moved', this.now(), (w) => {
      for (const id of tops) {
        const node = a.nodes.get(id)!;
        const position = between(lower, upper);
        lower = position;
        putNode(w, arrangementId, { ...node, parentID: parent ?? undefined, position });
      }
    }) ? ['project'] : [];
  }

  setFolder(nodeIds: string[], arrangementId: string, isFolder: boolean): string[] {
    const project = this.project();
    const a = this.arrangement(project, arrangementId);
    this.requireUnlockedNodes(a, nodeIds);
    if (!isFolder) this.keepPosts(a, convertingNodes(a, nodeIds, false), nodeIds);
    const nodes = nodeIds.map((id) => a.nodes.get(id)).filter((n): n is ArrangementNode => !!n && n.isFolder !== isFolder);
    if (!nodes.length) return [];
    return this.change('project', isFolder ? 'Converted to folder' : 'Converted to document', this.now(), (w) => {
      for (const node of nodes) putNode(w, arrangementId, { ...node, isFolder });
    }) ? ['project'] : [];
  }

  /** `keepPosts`: in a journal's manuscript, a move that would make a numbered post a section of another is refused, and
   *  so is making a document of a folder that holds posts (the same words as the Swift). */
  private keepPosts(before: Arrangement, after: Arrangement, converting: string[] = []): void {
    if (before.role !== 'manuscript' || this.project().projectType !== 'journal') return;
    const demoted = demotedPosts(before, after);
    if (!demoted.length) return;
    const title = (fragmentId: string | undefined, nodeId: string) => {
      const doc = fragmentId !== undefined ? this.docs.get(fragmentDocName(fragmentId)) : undefined;
      const t = (doc ? new SwiftReader(doc).string(ROOT, 'title') : undefined) ?? before.nodes.get(nodeId)?.title;
      return t || 'Untitled';
    };
    for (const id of converting) {
      const ancestors = (nodeId: string) => {
        const out: string[] = [];
        let n = before.nodes.get(nodeId);
        let p = n ? resolvedParent(before, n) : undefined;
        while (p !== undefined && !out.includes(p)) { out.push(p); n = before.nodes.get(p); p = n ? resolvedParent(before, n) : undefined; }
        return out;
      };
      const held = demoted.filter((d) => ancestors(d.post.nodeId).includes(id)).length;
      if (!held) continue;
      const name = title(before.nodes.get(id)?.fragmentID, id);
      throw new Error(held === 1 ? `“${name}” holds a post. A document can't hold posts.` : `“${name}” holds ${held} posts. A document can't hold posts.`);
    }
    for (const { post, holder } of demoted) {
      const doc = this.docs.get(fragmentDocName(post.fragmentId));
      const number = doc ? JournalPosts.number(readMetadata(new SwiftReader(doc))) : null;
      if (number === null) continue;
      throw new Error(`“${title(post.fragmentId, post.nodeId)}” is post #${number}. Move it beside “${title(holder.fragmentId, holder.nodeId)}” instead.`);
    }
  }

  trash(nodeIds: string[], arrangementId: string): string[] {
    const project = this.project();
    const source = this.arrangement(project, arrangementId);
    if (source.role === 'trash') return [];
    this.requireUnlockedNodes(source, nodeIds);
    const others = project.arrangements.filter((x) => x.id !== arrangementId && x.role !== 'trash');
    const existingTrash = project.arrangements.find((x) => x.role === 'trash');
    const trashID = existingTrash?.id ?? 'trash';
    const live: Arrangement = { ...source, nodes: new Map(source.nodes) };
    let last = existingTrash ? buildTree(existingTrash).at(-1)?.node.position : undefined;
    return this.change('project', 'Moved to Trash', this.now(), (w) => {
      if (!existingTrash) {
        // createArrangement(id: "trash", name: "Trash", role: .trash): ordered after every other arrangement.
        const order = Math.max(-1, ...project.arrangements.map((x) => x.order)) + 1;
        const path = ['arrangements', trashID];
        w.putIfChanged(path, 'name', str('Trash'));
        w.putIfChanged(path, 'role', str('trash'));
        w.putIfChanged(path, 'order', f64(order));
        w.ensure([...path, 'nodes']);
      }
      for (const nodeID of nodeIds) {
        // Read afresh: a node already gone with an earlier one's subtree has nothing left to trash.
        const doomed = subtree(live, nodeID);
        if (!doomed.length) continue;
        for (const n of doomed) { w.delete(['arrangements', arrangementId, 'nodes'], n.id); live.nodes.delete(n.id); }
        for (const node of doomed) {
          const fid = node.fragmentID;
          if (fid === undefined || others.some((o) => [...o.nodes.values()].some((n) => n.fragmentID === fid))) continue;
          if (!project.fragments.has(fid)) throw new Error(`Not found: fragment ${fid}`);
          w.putIfChanged(['fragments', fid], 'trashed', bool(true));
          const position = between(last, undefined);
          last = position;
          putNode(w, trashID, { id: this.id(), fragmentID: fid, isFolder: node.isFolder, position });
        }
      }
    }) ? ['project'] : [];
  }

  // MARK: History

  createCheckpoint(name: string, trigger = 'manual'): { id: string; changed: string[] } {
    const project = this.project();
    const at = this.now();
    // Every registered fragment with a document here: one not synced yet is left out (StateResolver reports it).
    const fragmentHeads: Record<string, string> = {};
    for (const id of project.fragments.keys()) {
      const doc = this.docs.get(fragmentDocName(id));
      if (doc) fragmentHeads[id] = encodeHeads(A.getHeads(doc));
    }
    const id = this.id();
    const projectHeads = encodeHeads(A.getHeads(this.doc('project')));
    this.change('project', `Checkpoint: ${name}`, at, (w, _r, d) => {
      // `addCheckpoint`: a new map (putObject) with every field put.
      if (!d.checkpoints) d.checkpoints = {};
      d.checkpoints[id] = {};
      const path = ['checkpoints', id];
      w.put(path, 'name', str(name));
      w.put(path, 'createdAt', timestamp(at));
      w.put(path, 'createdBy', str(this.context.author));
      w.put(path, 'trigger', str(trigger));
      w.put(path, 'epoch', int(project.epoch));
      w.put(path, 'projectHeads', str(projectHeads));
      d.checkpoints[id].fragmentHeads = {};
      for (const [fid, h] of Object.entries(fragmentHeads)) w.put([...path, 'fragmentHeads'], fid, str(h));
    });
    return { id, changed: ['project'] };
  }
}
